/**
 * Abonnements / prélèvements récurrents.
 *
 * La liste est détectée automatiquement (heuristique côté backend), puis
 * l'utilisateur peut la piloter :
 *   - tri par colonne ;
 *   - filtre par compte ;
 *   - annotation d'un abonnement (nom personnalisé + note) ;
 *   - statut actif / résilié (les résiliés vont dans un onglet dédié mais
 *     restent visibles) ;
 *   - création d'une action (To-Do) pré-remplie depuis un abonnement ;
 *   - création d'un CONTRAT (module Contrats) pré-rempli depuis un abonnement :
 *     lien persistant (badge « contrat lié »), et échéances injectées dans le
 *     budget à venir (Mon Budget → Opérations + Projection) côté backend.
 *
 * Monté comme onglet dans Mon Budget (consolidé) et utilisable par compte.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditNoteIcon from '@mui/icons-material/EditNote'
import AddTaskIcon from '@mui/icons-material/AddTask'
import CancelIcon from '@mui/icons-material/Cancel'
import ReplayIcon from '@mui/icons-material/Replay'
import DescriptionIcon from '@mui/icons-material/Description'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { makeGetRequest, makePostRequest } from 'utils/api'
import urls from 'utils/urls'
import { useTranslation } from 'react-i18next'

const P = {
  bg: 'linear-gradient(180deg, #0a1024 0%, #0e1a36 100%)',
  card: 'rgba(255,255,255,0.035)',
  border: 'rgba(255,255,255,0.10)',
  borderStrong: 'rgba(126,156,219,0.30)',
  text: '#e8eefc',
  textDim: '#93a4c8',
  accent: '#7e9cdb',
  warn: '#fbbf24',
  negative: '#f87171',
  mono: '"Roboto Mono","SFMono-Regular","Courier New",monospace',
}
const chf = (n) => `${Number(n || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CHF`

const PRIORITES = [
  'Important et urgent',
  'Important et non urgent',
  'Non-important et urgent',
  'Non-important et non-urgent',
]

// — Création de contrat depuis un abonnement (miroir des choices backend) —
const CONTRACT_TYPES = [
  { value: 'electricity', label: 'Électricité' },
  { value: 'gas', label: 'Gaz' },
  { value: 'water', label: 'Eau' },
  { value: 'heating', label: 'Chauffage / fioul' },
  { value: 'internet', label: 'Internet / box' },
  { value: 'mobile', label: 'Téléphonie mobile' },
  { value: 'insurance', label: 'Assurance' },
  { value: 'streaming', label: 'Abonnement média' },
  { value: 'other', label: 'Autre' },
]
const CONTRACT_FREQUENCIES = [
  { value: 'monthly', label: 'Mensuel' },
  { value: 'bimonthly', label: 'Bimestriel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'semiannual', label: 'Semestriel' },
  { value: 'annual', label: 'Annuel' },
]

// Devine le type de contrat à partir du libellé détecté.
const TYPE_KEYWORDS = [
  { type: 'insurance', words: ['assur', 'axa', 'allianz', 'generali', 'zurich', 'helvetia', 'mobiliere', 'mobilière', 'vaudoise', 'css', 'helsana', 'sanitas', 'assura', 'groupe mutuel', 'swica', 'lamal'] },
  { type: 'streaming', words: ['netflix', 'spotify', 'disney', 'apple', 'youtube', 'prime', 'canal', 'deezer', 'hbo', 'paramount'] },
  { type: 'mobile', words: ['swisscom', 'sunrise', 'salt', 'wingo', 'yallo', 'mobile'] },
  { type: 'internet', words: ['internet', 'fibre', 'box', 'init7', 'quickline'] },
  { type: 'electricity', words: ['electricite', 'électricité', 'romande energie', 'groupe e', 'sig ', 'bkw'] },
  { type: 'gas', words: ['gaz'] },
  { type: 'water', words: ['eau '] },
  { type: 'heating', words: ['chauffage', 'fioul', 'mazout'] },
]
const guessContractType = (label) => {
  const l = ` ${String(label || '').toLowerCase()} `
  const hit = TYPE_KEYWORDS.find((k) => k.words.some((w) => l.includes(w)))
  return hit ? hit.type : 'other'
}

// Fréquence détectée (FR) → fréquence contrat. Les fréquences sans équivalent
// (hebdomadaire, bimensuel) sont mensualisées : monthly + coût mensuel.
const mapFrequency = (freq) => {
  if (freq === 'trimestriel') return { value: 'quarterly', mensualized: false }
  if (freq === 'annuel') return { value: 'annual', mensualized: false }
  if (freq === 'mensuel') return { value: 'monthly', mensualized: false }
  return { value: 'monthly', mensualized: true } // hebdomadaire, bimensuel…
}
const FREQ_STEP_MONTHS = { monthly: 1, bimonthly: 2, quarterly: 3, semiannual: 6, annual: 12 }

// Prochaine échéance = dernier prélèvement + pas de fréquence (dans le futur).
const computeNextDue = (lastDateIso, contractFreq) => {
  if (!lastDateIso) return ''
  const step = FREQ_STEP_MONTHS[contractFreq] || 1
  const d = new Date(`${lastDateIso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return ''
  const today = new Date()
  let guard = 0
  do {
    d.setMonth(d.getMonth() + step)
    guard += 1
  } while (d <= today && guard < 24)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd}`
}

// Colonnes triables : clé de tri + type (num / text).
const COLUMNS = [
  { id: 'display_name', labelKey: 'name', labelDflt: 'Abonnement', type: 'text', align: 'left' },
  { id: 'frequency', labelKey: 'frequency', labelDflt: 'Fréquence', type: 'text', align: 'left' },
  { id: 'amount', labelKey: 'amount', labelDflt: 'Montant', type: 'num', align: 'right' },
  { id: 'monthly_cost', labelKey: 'monthly', labelDflt: '/ mois', type: 'num', align: 'right' },
  { id: 'annual_cost', labelKey: 'annual', labelDflt: '/ an', type: 'num', align: 'right' },
  { id: 'occurrences', labelKey: 'occurrences', labelDflt: 'Occur.', type: 'num', align: 'center' },
  { id: 'last_date', labelKey: 'lastDate', labelDflt: 'Dernier', type: 'text', align: 'left' },
]

export default function Subscriptions({ accountId = null }) {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const tr = (k, dflt) => t(`finance.subscriptions.${k}`, dflt)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [accounts, setAccounts] = useState([])
  const [accountFilter, setAccountFilter] = useState(accountId || 'all')
  const [statusTab, setStatusTab] = useState('actif')
  const [sortBy, setSortBy] = useState('annual_cost')
  const [sortDir, setSortDir] = useState('desc')

  // Menu d'actions par ligne
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [menuSub, setMenuSub] = useState(null)

  // Dialog annotation
  const [flagOpen, setFlagOpen] = useState(false)
  const [flagSub, setFlagSub] = useState(null)
  const [flagName, setFlagName] = useState('')
  const [flagStatus, setFlagStatus] = useState('actif')
  const [flagNote, setFlagNote] = useState('')
  const [flagSaving, setFlagSaving] = useState(false)

  // Dialog création tâche
  const [todoOpen, setTodoOpen] = useState(false)
  const [todoNom, setTodoNom] = useState('')
  const [todoDesc, setTodoDesc] = useState('')
  const [todoDate, setTodoDate] = useState('')
  const [todoPriorite, setTodoPriorite] = useState('Important et non urgent')
  const [todoSaving, setTodoSaving] = useState(false)

  // Dialog création contrat
  const [contractOpen, setContractOpen] = useState(false)
  const [contractSub, setContractSub] = useState(null)
  const [contractName, setContractName] = useState('')
  const [contractType, setContractType] = useState('other')
  const [contractProvider, setContractProvider] = useState('')
  const [contractPrice, setContractPrice] = useState('')
  const [contractFreq, setContractFreq] = useState('monthly')
  const [contractNextDue, setContractNextDue] = useState('')
  const [contractSubcat, setContractSubcat] = useState('')
  const [contractInBudget, setContractInBudget] = useState(true)
  const [contractMensualized, setContractMensualized] = useState(false)
  const [contractSaving, setContractSaving] = useState(false)

  const [toast, setToast] = useState(null) // { severity, msg }

  const fetchAccounts = useCallback(async () => {
    const { data: res, status } = await makeGetRequest(urls.fetchAccounts, {}, true)
    if (status === 200 && Array.isArray(res)) {
      setAccounts(
        res.map((a) => ({ id: a.id, name: a.account_name || a.NumeroCompte || `#${a.id}` })),
      )
    }
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    let url = urls.financeSubscriptions
    if (accountFilter && accountFilter !== 'all') url += `?account=${accountFilter}`
    const { data: res, status } = await makeGetRequest(url, {}, true)
    if (status === 200) setData(res)
    setLoading(false)
  }, [accountFilter])

  useEffect(() => { fetchAccounts() }, [fetchAccounts])
  useEffect(() => { fetchData() }, [fetchData])

  const allSubs = (data && data.subscriptions) || []
  const counts = useMemo(() => ({
    actif: allSubs.filter((s) => s.status !== 'resilie').length,
    resilie: allSubs.filter((s) => s.status === 'resilie').length,
  }), [allSubs])

  const visibleSubs = useMemo(() => {
    const wanted = statusTab === 'resilie' ? 'resilie' : 'actif'
    const filtered = allSubs.filter((s) =>
      wanted === 'resilie' ? s.status === 'resilie' : s.status !== 'resilie',
    )
    const col = COLUMNS.find((c) => c.id === sortBy)
    const type = col ? col.type : 'num'
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      let va = a[sortBy]
      let vb = b[sortBy]
      if (type === 'num') {
        va = Number(va || 0)
        vb = Number(vb || 0)
        return (va - vb) * dir
      }
      va = String(va || '').toLowerCase()
      vb = String(vb || '').toLowerCase()
      return va.localeCompare(vb) * dir
    })
  }, [allSubs, statusTab, sortBy, sortDir])

  const handleSort = (colId) => {
    if (sortBy === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(colId)
      setSortDir(colId === 'display_name' || colId === 'frequency' ? 'asc' : 'desc')
    }
  }

  const openMenu = (e, sub) => { setMenuAnchor(e.currentTarget); setMenuSub(sub) }
  const closeMenu = () => { setMenuAnchor(null); setMenuSub(null) }

  // — Annotation —
  const openFlag = (sub) => {
    setFlagSub(sub)
    setFlagName(sub.custom_name || '')
    setFlagStatus(sub.status || 'actif')
    setFlagNote(sub.note || '')
    setFlagOpen(true)
    closeMenu()
  }
  const saveFlag = async () => {
    if (!flagSub) return
    setFlagSaving(true)
    const { status } = await makePostRequest(
      urls.financeSubscriptionFlag,
      { sub_key: flagSub.key, custom_name: flagName, status: flagStatus, note: flagNote },
      {},
      true,
    )
    setFlagSaving(false)
    if (status === 200) {
      setFlagOpen(false)
      setToast({ severity: 'success', msg: tr('flagSaved', 'Abonnement mis à jour.') })
      fetchData()
    } else {
      setToast({ severity: 'error', msg: tr('flagError', "Échec de l'enregistrement.") })
    }
  }

  // — Bascule rapide de statut —
  const quickStatus = async (sub, newStatus) => {
    closeMenu()
    const { status } = await makePostRequest(
      urls.financeSubscriptionFlag,
      { sub_key: sub.key, status: newStatus },
      {},
      true,
    )
    if (status === 200) {
      setToast({
        severity: 'success',
        msg: newStatus === 'resilie'
          ? tr('markedResilie', 'Abonnement marqué résilié.')
          : tr('markedActif', 'Abonnement réactivé.'),
      })
      fetchData()
    } else {
      setToast({ severity: 'error', msg: tr('flagError', "Échec de l'enregistrement.") })
    }
  }

  // — Création tâche —
  const openTodo = (sub) => {
    const name = sub.display_name || sub.label
    setTodoNom(`${tr('todoPrefix', 'Abonnement')} : ${name}`)
    setTodoDesc(
      tr('todoDesc', 'Abonnement récurrent ({{freq}}) — {{m}}/mois ({{a}}/an). Compte : {{acc}}.')
        .replace('{{freq}}', sub.frequency || '')
        .replace('{{m}}', chf(sub.monthly_cost))
        .replace('{{a}}', chf(sub.annual_cost))
        .replace('{{acc}}', sub.account || '—'),
    )
    setTodoDate('')
    setTodoPriorite('Important et non urgent')
    setTodoOpen(true)
    closeMenu()
  }
  const saveTodo = async () => {
    if (!todoNom.trim()) return
    setTodoSaving(true)
    const body = {
      Nom: todoNom.trim().slice(0, 80),
      Descriptions: todoDesc,
      Priorite: todoPriorite,
      Statut: 'Non démarré',
      TypeTache: 'livrable',
      Done: false,
    }
    if (todoDate) body.DateLimite = todoDate
    const { status } = await makePostRequest(urls.taskOperations, body, {}, true)
    setTodoSaving(false)
    if (status === 200 || status === 201) {
      setTodoOpen(false)
      setToast({ severity: 'success', msg: tr('todoCreated', 'Action créée dans To-Do.') })
    } else {
      setToast({ severity: 'error', msg: tr('todoError', "Échec de la création de l'action.") })
    }
  }

  // — Création contrat —
  const openContract = (sub) => {
    const name = sub.display_name || sub.label
    const freq = mapFrequency(sub.frequency)
    setContractSub(sub)
    setContractName(name)
    setContractType(guessContractType(name))
    setContractProvider(name)
    setContractPrice(String(freq.mensualized ? Number(sub.monthly_cost || 0).toFixed(2) : Number(sub.amount || 0).toFixed(2)))
    setContractFreq(freq.value)
    setContractNextDue(computeNextDue(sub.last_date, freq.value))
    setContractSubcat(sub.subcategory_guess || '')
    setContractInBudget(true)
    setContractMensualized(freq.mensualized)
    setContractOpen(true)
    closeMenu()
  }
  const saveContract = async () => {
    if (!contractSub || !contractName.trim() || !Number(contractPrice)) return
    setContractSaving(true)
    const { data: res, status } = await makePostRequest(
      urls.financeSubscriptionCreateContract,
      {
        sub_key: contractSub.key,
        name: contractName.trim(),
        contract_type: contractType,
        provider: contractProvider.trim(),
        subscription_price: contractPrice,
        payment_frequency: contractFreq,
        next_due_date: contractNextDue || null,
        budget_subcategory: contractSubcat,
        include_in_budget: contractInBudget,
      },
      {},
      true,
    )
    setContractSaving(false)
    if (status === 201) {
      setContractOpen(false)
      setToast({
        severity: 'success',
        msg: tr('contractCreated', 'Contrat « {{x}} » créé — échéances ajoutées au budget à venir.')
          .replace('{{x}}', (res && res.contract_name) || contractName.trim()),
      })
      fetchData()
    } else if (status === 400 && res && res.detail) {
      setToast({ severity: 'error', msg: res.detail })
    } else {
      setToast({ severity: 'error', msg: tr('contractError', 'Échec de la création du contrat.') })
    }
  }

  const showAccountFilter = !accountId // masqué si le composant est déjà borné à un compte

  return (
    <Box sx={{ background: P.bg, borderRadius: 2, p: { xs: 1.5, sm: 2.5 }, color: P.text }}>
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ color: P.text, fontWeight: 700, fontSize: 17 }}>{tr('title', 'Abonnements & prélèvements récurrents')}</Typography>
          <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>{tr('subtitle', 'Détectés automatiquement à partir de vos opérations récurrentes.')}</Typography>
        </Box>
        {showAccountFilter && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: P.textDim }}>{tr('account', 'Compte')}</InputLabel>
            <Select
              value={accountFilter}
              label={tr('account', 'Compte')}
              onChange={(e) => setAccountFilter(e.target.value)}
              sx={{
                color: P.text,
                '.MuiOutlinedInput-notchedOutline': { borderColor: P.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: P.borderStrong },
                '.MuiSvgIcon-root': { color: P.textDim },
              }}
            >
              <MenuItem value="all">{tr('allAccounts', 'Tous les comptes')}</MenuItem>
              {accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={36} sx={{ color: P.accent }} /></Box>
      ) : allSubs.length === 0 ? (
        <Typography sx={{ color: P.textDim, fontSize: 13 }}>{tr('none', 'Aucun abonnement récurrent détecté.')}</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Kpi label={tr('count', 'Abonnements actifs')} value={data.count} raw />
            <Kpi label={tr('totalMonthly', 'Coût mensuel')} value={data.total_monthly} highlight />
            <Kpi label={tr('totalAnnual', 'Coût annuel')} value={data.total_annual} />
          </Stack>

          <Tabs
            value={statusTab}
            onChange={(e, v) => setStatusTab(v)}
            sx={{
              minHeight: 36,
              mb: 1,
              '& .MuiTab-root': { color: P.textDim, textTransform: 'none', minHeight: 36, fontWeight: 600 },
              '& .Mui-selected': { color: `${P.text} !important` },
              '& .MuiTabs-indicator': { bgcolor: P.accent },
            }}
          >
            <Tab value="actif" label={`${tr('tabActive', 'Actifs')} (${counts.actif})`} />
            <Tab value="resilie" label={`${tr('tabResilie', 'Résiliés')} (${counts.resilie})`} />
          </Tabs>

          {visibleSubs.length === 0 ? (
            <Typography sx={{ color: P.textDim, fontSize: 13, py: 2 }}>
              {statusTab === 'resilie'
                ? tr('noneResilie', 'Aucun abonnement résilié.')
                : tr('noneActive', 'Aucun abonnement actif.')}
            </Typography>
          ) : (
            <Box sx={{ bgcolor: P.card, border: `1px solid ${P.border}`, borderRadius: 2, p: 1, overflowX: 'auto' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <Th key={col.id} align={col.align}>
                          <TableSortLabel
                            active={sortBy === col.id}
                            direction={sortBy === col.id ? sortDir : 'asc'}
                            onClick={() => handleSort(col.id)}
                            sx={{
                              color: `${P.textDim} !important`,
                              '& .MuiTableSortLabel-icon': { color: `${P.accent} !important` },
                              '&.Mui-active': { color: `${P.text} !important` },
                            }}
                          >
                            {tr(col.labelKey, col.labelDflt)}
                          </TableSortLabel>
                        </Th>
                      ))}
                      <Th align="center">{tr('actions', 'Actions')}</Th>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleSubs.map((s) => {
                      const resilie = s.status === 'resilie'
                      return (
                        <TableRow key={s.key} hover sx={{ '& td': { borderColor: P.border, color: P.text }, opacity: resilie ? 0.55 : 1 }}>
                          <TableCell sx={{ color: P.text }}>
                            <Stack direction="row" alignItems="center" spacing={0.8}>
                              <Typography sx={{ fontSize: 13, color: P.text, textDecoration: resilie ? 'line-through' : 'none' }}>
                                {s.display_name || s.label}
                              </Typography>
                              {s.custom_name && (
                                <Tooltip title={tr('detectedAs', 'Détecté comme : {{x}}').replace('{{x}}', s.label)}>
                                  <Chip size="small" label={tr('renamed', 'renommé')} sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(126,156,219,0.15)', color: P.accent }} />
                                </Tooltip>
                              )}
                              {resilie && (
                                <Chip size="small" label={tr('resilie', 'résilié')} sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(248,113,113,0.18)', color: P.negative }} />
                              )}
                              {s.contract_id && (
                                <Tooltip title={tr('contractLinkedTip', 'Contrat lié : {{x}} — échéances dans le budget à venir').replace('{{x}}', s.contract_name || '')}>
                                  <Chip
                                    size="small"
                                    icon={<DescriptionIcon sx={{ fontSize: 13 }} />}
                                    label={tr('contractLinked', 'contrat lié')}
                                    onClick={() => navigate('/contrats')}
                                    sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(74,222,128,0.15)', color: '#4ade80', cursor: 'pointer', '& .MuiChip-icon': { color: '#4ade80' } }}
                                  />
                                </Tooltip>
                              )}
                              {s.increased && !resilie && (
                                <Tooltip title={tr('increasedTip', 'Le montant a augmenté ({{a}} → {{b}})').replace('{{a}}', chf(s.first_amount)).replace('{{b}}', chf(s.last_amount))}>
                                  <Chip size="small" icon={<TrendingUpIcon sx={{ fontSize: 14 }} />} label={tr('increased', 'Hausse')} sx={{ height: 20, bgcolor: 'rgba(251,191,36,0.18)', color: P.warn, '& .MuiChip-icon': { color: P.warn } }} />
                                </Tooltip>
                              )}
                            </Stack>
                            <Typography sx={{ color: P.textDim, fontSize: 10.5 }} noWrap>{s.account}</Typography>
                          </TableCell>
                          <TableCell sx={{ color: P.textDim, fontSize: 12 }}>{s.frequency}</TableCell>
                          <Td>{chf(s.amount)}</Td>
                          <Td>{chf(s.monthly_cost)}</Td>
                          <Td bold>{chf(s.annual_cost)}</Td>
                          <TableCell align="center" sx={{ color: P.textDim, fontFamily: P.mono, fontSize: 12 }}>{s.occurrences}</TableCell>
                          <TableCell sx={{ color: P.textDim, fontSize: 12, whiteSpace: 'nowrap' }}>{s.last_date}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={(e) => openMenu(e, s)} sx={{ color: P.textDim }}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          <Typography sx={{ color: P.textDim, fontSize: 11, mt: 1, fontStyle: 'italic' }}>
            {tr('hint', 'Détection heuristique : un prélèvement régulier (≥ 3 fois, montant stable). Ajustez vos catégories pour affiner.')}
          </Typography>
        </>
      )}

      {/* Menu d'actions par ligne */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => openFlag(menuSub)}>
          <EditNoteIcon fontSize="small" style={{ marginRight: 8 }} /> {tr('annotate', 'Renommer / annoter')}
        </MenuItem>
        <MenuItem onClick={() => openTodo(menuSub)}>
          <AddTaskIcon fontSize="small" style={{ marginRight: 8 }} /> {tr('createTodo', 'Créer une action (To-Do)')}
        </MenuItem>
        {menuSub && menuSub.contract_id ? (
          <MenuItem onClick={() => { closeMenu(); navigate('/contrats') }}>
            <OpenInNewIcon fontSize="small" style={{ marginRight: 8 }} /> {tr('viewContract', 'Voir le contrat')}
          </MenuItem>
        ) : (
          <MenuItem onClick={() => openContract(menuSub)}>
            <DescriptionIcon fontSize="small" style={{ marginRight: 8 }} /> {tr('createContract', 'Créer un contrat')}
          </MenuItem>
        )}
        {menuSub && menuSub.status === 'resilie' ? (
          <MenuItem onClick={() => quickStatus(menuSub, 'actif')}>
            <ReplayIcon fontSize="small" style={{ marginRight: 8 }} /> {tr('reactivate', 'Réactiver')}
          </MenuItem>
        ) : (
          <MenuItem onClick={() => quickStatus(menuSub, 'resilie')}>
            <CancelIcon fontSize="small" style={{ marginRight: 8 }} /> {tr('markResilie', 'Marquer résilié')}
          </MenuItem>
        )}
      </Menu>

      {/* Dialog annotation */}
      <Dialog open={flagOpen} onClose={() => setFlagOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{tr('annotateTitle', "Annoter l'abonnement")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={tr('customName', 'Nom personnalisé')}
              value={flagName}
              onChange={(e) => setFlagName(e.target.value)}
              placeholder={flagSub ? flagSub.label : ''}
              size="small"
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{tr('status', 'Statut')}</InputLabel>
              <Select value={flagStatus} label={tr('status', 'Statut')} onChange={(e) => setFlagStatus(e.target.value)}>
                <MenuItem value="actif">{tr('statusActive', 'Actif')}</MenuItem>
                <MenuItem value="resilie">{tr('statusResilie', 'Résilié')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={tr('note', 'Note')}
              value={flagNote}
              onChange={(e) => setFlagNote(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagOpen(false)}>{tr('cancel', 'Annuler')}</Button>
          <Button onClick={saveFlag} variant="contained" disabled={flagSaving}>
            {flagSaving ? tr('saving', 'Enregistrement…') : tr('save', 'Enregistrer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog création tâche */}
      <Dialog open={todoOpen} onClose={() => setTodoOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{tr('todoTitle', 'Créer une action depuis cet abonnement')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={tr('todoName', 'Intitulé')}
              value={todoNom}
              onChange={(e) => setTodoNom(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              label={tr('todoDescription', 'Description')}
              value={todoDesc}
              onChange={(e) => setTodoDesc(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label={tr('todoDeadline', 'Échéance')}
              type="date"
              value={todoDate}
              onChange={(e) => setTodoDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>{tr('todoPriority', 'Priorité')}</InputLabel>
              <Select value={todoPriorite} label={tr('todoPriority', 'Priorité')} onChange={(e) => setTodoPriorite(e.target.value)}>
                {PRIORITES.map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTodoOpen(false)}>{tr('cancel', 'Annuler')}</Button>
          <Button onClick={saveTodo} variant="contained" disabled={todoSaving || !todoNom.trim()}>
            {todoSaving ? tr('creating', 'Création…') : tr('create', 'Créer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog création contrat */}
      <Dialog open={contractOpen} onClose={() => setContractOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{tr('contractTitle', 'Créer un contrat depuis cet abonnement')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={tr('contractName', 'Nom du contrat')}
              value={contractName}
              onChange={(e) => setContractName(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ maxLength: 160 }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>{tr('contractType', 'Type')}</InputLabel>
                <Select value={contractType} label={tr('contractType', 'Type')} onChange={(e) => setContractType(e.target.value)}>
                  {CONTRACT_TYPES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={tr('contractProvider', 'Fournisseur')}
                value={contractProvider}
                onChange={(e) => setContractProvider(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ maxLength: 160 }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={tr('contractPrice', 'Prix par échéance (CHF)')}
                value={contractPrice}
                onChange={(e) => setContractPrice(e.target.value)}
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>{tr('contractFreq', 'Fréquence')}</InputLabel>
                <Select value={contractFreq} label={tr('contractFreq', 'Fréquence')} onChange={(e) => setContractFreq(e.target.value)}>
                  {CONTRACT_FREQUENCIES.map((f) => (
                    <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {contractMensualized && (
              <Alert severity="info" sx={{ py: 0 }}>
                {tr('contractMensualized', 'Fréquence détectée « {{x}} » sans équivalent contrat : mensualisée automatiquement.')
                  .replace('{{x}}', (contractSub && contractSub.frequency) || '')}
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={tr('contractNextDue', 'Prochaine échéance')}
                type="date"
                value={contractNextDue}
                onChange={(e) => setContractNextDue(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label={tr('contractSubcat', 'Sous-catégorie budget')}
                value={contractSubcat}
                onChange={(e) => setContractSubcat(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ maxLength: 50 }}
              />
            </Stack>
            <FormControlLabel
              control={<Switch checked={contractInBudget} onChange={(e) => setContractInBudget(e.target.checked)} />}
              label={tr('contractInBudget', 'Injecter les échéances dans le budget à venir (Opérations + Projection)')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractOpen(false)}>{tr('cancel', 'Annuler')}</Button>
          <Button
            onClick={saveContract}
            variant="contained"
            disabled={contractSaving || !contractName.trim() || !Number(contractPrice)}
          >
            {contractSaving ? tr('creating', 'Création…') : tr('createContractBtn', 'Créer le contrat')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : null}
      </Snackbar>
    </Box>
  )
}

function Th({ children, align }) {
  return <TableCell align={align} sx={{ color: P.textDim, fontWeight: 600, borderColor: P.border, whiteSpace: 'nowrap' }}>{children}</TableCell>
}
function Td({ children, bold }) {
  return <TableCell align="right" sx={{ color: P.text, fontFamily: P.mono, fontVariantNumeric: 'tabular-nums', fontWeight: bold ? 700 : 400, borderColor: P.border, whiteSpace: 'nowrap' }}>{children}</TableCell>
}
function Kpi({ label, value, highlight, raw }) {
  return (
    <Box sx={{ flex: '1 1 160px', minWidth: 150, bgcolor: highlight ? 'rgba(126,156,219,0.12)' : P.card, border: `1px solid ${highlight ? P.borderStrong : P.border}`, borderRadius: 2, p: 1.5 }}>
      <Typography sx={{ color: P.textDim, fontSize: 11 }}>{label}</Typography>
      <Typography sx={{ color: P.text, fontSize: 18, fontWeight: 700, fontFamily: P.mono, mt: 0.4 }}>{raw ? value : chf(value)}</Typography>
    </Box>
  )
}
